/**
 * Light on contact: a short additive glow and a point light at the spot a
 * strike met the body, pinned to the fighter it hit. White when a guard took
 * it (block or parry); otherwise from yellow to red by how much of the struck
 * part's health the hit took.
 */
import { PART_MAX_HP, rotateY, type Vec3 } from "@meleeroyale/sim";
import * as THREE from "three";

/** Flashes alive at once; the oldest is reused. Lights are pooled so shaders never recompile. */
const POOL = 4;
const LIFE_MS = 260;
const LIGHT_REACH = 0.9;
const GUARD_LIFE_MS = 180;
/** A hit that takes this share of the part's starting HP is fully red. */
const RED_AT = 0.3;
const YELLOW_HUE = 55 / 360;

export interface HitFlashEvent {
  target: number;
  part: number;
  /** Contact point in the target's local frame. */
  at: Vec3;
  damage: number;
  blocked: boolean;
  parried: boolean;
}

/** 0 .. 1: how much of the struck part this hit took, relative to `RED_AT`. */
export function hitSeverity(damage: number, part: number): number {
  const max = PART_MAX_HP[part] ?? 100;
  return Math.max(0, Math.min(1, damage / (max * RED_AT)));
}

export function hitColor(hit: HitFlashEvent, out = new THREE.Color()): THREE.Color {
  if (hit.blocked || hit.parried) return out.setRGB(1, 1, 1);
  const severity = hitSeverity(hit.damage, hit.part);
  return out.setHSL(YELLOW_HUE * (1 - severity), 1, 0.55);
}

interface Flash {
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  light: THREE.PointLight;
  target: number;
  local: Vec3;
  startAt: number;
  life: number;
  size: number;
  peak: number;
  active: boolean;
}

export type TargetPose = (id: number) => { pos: Vec3; facing: number } | null;

export class HitFlashes {
  private readonly flashes: Flash[] = [];
  private readonly texture: THREE.CanvasTexture;
  private next = 0;

  constructor(scene: THREE.Scene) {
    this.texture = glowTexture();
    for (let i = 0; i < POOL; i += 1) {
      const material = new THREE.SpriteMaterial({
        map: this.texture,
        color: 0xffffff,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
        opacity: 0,
      });
      const sprite = new THREE.Sprite(material);
      sprite.renderOrder = 20;
      sprite.visible = false;
      // A short reach: toon shading snaps any strong light to full brightness, so a
      // wide light would paint the whole fighter instead of the spot that was hit.
      const light = new THREE.PointLight(0xffffff, 0, LIGHT_REACH, 2);
      scene.add(sprite, light);
      this.flashes.push({
        sprite,
        material,
        light,
        target: -1,
        local: { x: 0, y: 0, z: 0 },
        startAt: 0,
        life: LIFE_MS,
        size: 0.4,
        peak: 0,
        active: false,
      });
    }
  }

  /** Shows a flash `delayMs` from `now` (remote hits wait for the interpolated view). */
  spawn(hit: HitFlashEvent, now: number, delayMs: number): void {
    const f = this.flashes[this.next];
    if (!f) return;
    this.next = (this.next + 1) % POOL;
    const guarded = hit.blocked || hit.parried;
    const severity = guarded ? 0.3 : hitSeverity(hit.damage, hit.part);
    const color = hitColor(hit);
    f.material.color.copy(color);
    f.light.color.copy(color);
    f.target = hit.target;
    f.local = { ...hit.at };
    f.startAt = now + delayMs;
    f.life = guarded ? GUARD_LIFE_MS : LIFE_MS;
    f.size = 0.4 + 0.6 * severity;
    f.peak = guarded ? 1.5 : 1.2 + 2.5 * severity;
    f.active = true;
  }

  /** Where a hit lands in world space, pinned to the target's current pose. */
  static world(pose: { pos: Vec3; facing: number }, local: Vec3): Vec3 {
    const w = rotateY(local, Math.sin(pose.facing), Math.cos(pose.facing));
    return { x: pose.pos.x + w.x, y: pose.pos.y + w.y, z: pose.pos.z + w.z };
  }

  update(now: number, poseOf: TargetPose): void {
    for (const f of this.flashes) {
      if (!f.active) continue;
      const t = (now - f.startAt) / f.life;
      const pose = poseOf(f.target);
      if (t >= 1 || !pose) {
        f.active = false;
        f.sprite.visible = false;
        f.light.intensity = 0;
        continue;
      }
      if (t < 0) {
        f.sprite.visible = false;
        f.light.intensity = 0;
        continue;
      }
      const p = HitFlashes.world(pose, f.local);
      const fade = (1 - t) * (1 - t);
      f.sprite.visible = true;
      f.sprite.position.set(p.x, p.y, p.z);
      f.sprite.scale.setScalar(f.size * (0.6 + 0.9 * t));
      f.material.opacity = fade;
      f.light.position.set(p.x, p.y, p.z);
      f.light.intensity = f.peak * fade;
    }
  }

  dispose(): void {
    for (const f of this.flashes) f.material.dispose();
    this.texture.dispose();
  }
}

/** A soft round glow: white core, transparent edge. */
function glowTexture(): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, "rgba(255,255,255,1)");
    g.addColorStop(0.25, "rgba(255,255,255,0.85)");
    g.addColorStop(0.6, "rgba(255,255,255,0.25)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
